import { useEffect } from 'react'
import { OG_IMAGE_URL, SITE_URL, upsertLink, upsertMeta, removeLink, upsertJsonLd, removeJsonLd, breadcrumbJsonLd } from './useSeo'
import { getBlogPostsByTopic, blogListPath, blogPostPath } from '../content/blog'
import type { BlogPost } from '../content/blog/types'
import { SUPPORTED_LANGUAGES } from '../i18n'

const JSON_LD_ID = 'blog-post-jsonld'
const BREADCRUMB_ID = 'blog-post-breadcrumb'

/** Sets per-post SEO tags for /blog/:slug, /pl/blog/:slug and /es/blog/:slug. Separate from useSeo
 *  (which drives the i18n-translated app shell) because blog content is static-per-post text
 *  rather than translation keys - see content/blog/types.ts. Also sets hreflang alternates to any
 *  translated siblings (same topicId, see content/blog/index.ts), same pattern as
 *  useLandingPageSeo.ts - not every post has a sibling in every language. */
export function useBlogSeo(post: BlogPost) {
  useEffect(() => {
    document.documentElement.lang = post.lang

    const title = `${post.title} | Roofora Blog`
    const canonicalUrl = `${SITE_URL}${blogPostPath(post)}`

    document.title = title
    upsertMeta('name', 'description', post.metaDescription)
    upsertMeta('name', 'keywords', post.keywords.join(', '))
    upsertMeta('property', 'og:title', post.title)
    upsertMeta('property', 'og:description', post.metaDescription)
    upsertMeta('property', 'og:url', canonicalUrl)
    upsertMeta('property', 'og:type', 'article')
    upsertMeta('property', 'og:image', OG_IMAGE_URL)
    upsertMeta('name', 'twitter:title', post.title)
    upsertMeta('name', 'twitter:description', post.metaDescription)
    upsertMeta('name', 'twitter:image', OG_IMAGE_URL)
    upsertLink('canonical', canonicalUrl)

    // Not every post has a sibling in every language (see content/blog/index.ts) - a missing
    // language must actively remove any stale alternate tag rather than leave whatever was there
    // before (e.g. index.html's static default homepage hreflang links), which would otherwise
    // misdirect search engines to an unrelated page.
    const siblings = getBlogPostsByTopic(post.topicId)
    for (const lang of SUPPORTED_LANGUAGES) {
      const sibling = siblings.find((p) => p.lang === lang)
      if (sibling) upsertLink('alternate', `${SITE_URL}${blogPostPath(sibling)}`, lang)
      else removeLink('alternate', lang)
    }
    const deVariant = siblings.find((p) => p.lang === 'de')
    upsertLink('alternate', deVariant ? `${SITE_URL}${blogPostPath(deVariant)}` : canonicalUrl, 'x-default')

    // image/author/publisher.logo are required by Google's Article structured-data guidelines -
    // a site-audit crawl flagged "Structured data has schema.org validation error" across every
    // blog post (see docs/tasks/active/seo/optmierung/07) because all three were previously missing. No
    // per-post author persona exists in the content model (see content/blog/types.ts) - Roofora
    // itself is the author/publisher, same as any company blog with no individual bylines.
    upsertJsonLd(JSON_LD_ID, {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.metaDescription,
      image: OG_IMAGE_URL,
      datePublished: post.publishedAt,
      dateModified: post.publishedAt,
      inLanguage: post.lang,
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
      author: { '@type': 'Organization', name: 'Roofora', url: SITE_URL },
      publisher: {
        '@type': 'Organization',
        name: 'Roofora',
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.svg` },
      },
    })

    upsertJsonLd(
      BREADCRUMB_ID,
      breadcrumbJsonLd([
        { name: 'Roofora', url: `${SITE_URL}/${post.lang}` },
        { name: 'Blog', url: `${SITE_URL}${blogListPath(post.lang)}` },
        { name: post.title, url: canonicalUrl },
      ]),
    )

    return () => {
      removeJsonLd(JSON_LD_ID)
      removeJsonLd(BREADCRUMB_ID)
    }
  }, [post])
}
